import { motion } from 'motion/react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { createContext, useContext, useState, type ReactElement } from 'react';

import { isMacPlatform, type KeyName } from '@/lib/key';
import { cn } from '@/lib/utils';

import type { KeyTooltipProps } from '../registry';

// Long enough that a pointer passing over the buttons opens nothing, short enough to beat the app's usual tooltip
const OPEN_DELAY_MS = 300;

const MAC_KEYS: Partial<Record<KeyName, string>> = {
  mod: '⌘',
  shift: 'shift',
  alt: 'option',
  enter: 'enter',
  backspace: 'delete',
};

const OTHER_KEYS: Partial<Record<KeyName, string>> = {
  mod: 'ctrl',
  shift: 'shift',
  alt: 'alt',
  enter: 'enter',
  backspace: 'backspace',
};

const keyText = (key: KeyName) =>
  (isMacPlatform() ? MAC_KEYS : OTHER_KEYS)[key] ??
  (key.length === 1 ? key.toUpperCase() : key);

// Off on a card whose keys another card answers: one not highlighted in an expanded feed, or a History card, which
// answers none. Its tooltips then name the action alone
export const FeedKeysAnsweredContext = createContext(true);

// The feed's own tooltip, after Superhuman's: the action and its keys as soft chips, on one line. It stays dark
// in both themes: inverse on a light page, a raised surface on a dark one
export const FeedKeyTooltip = ({
  label,
  keys: allKeys,
  children,
}: KeyTooltipProps & { children: ReactElement }) => {
  const [open, setOpen] = useState(false);
  const keys = useContext(FeedKeysAnsweredContext) ? allKeys : undefined;

  return (
    <TooltipPrimitive.Root
      delayDuration={OPEN_DELAY_MS}
      disableHoverableContent
      open={open}
      onOpenChange={setOpen}
    >
      <TooltipPrimitive.Trigger
        asChild
        // A click's focus is not a reason to explain the button; a tab's is
        onFocus={(event) => {
          if (!event.currentTarget.matches(':focus-visible'))
            event.preventDefault();
        }}
      >
        {children}
      </TooltipPrimitive.Trigger>
      {open ? (
        <TooltipPrimitive.Portal forceMount>
          <TooltipPrimitive.Content
            side="bottom"
            align="center"
            sideOffset={6}
            collisionPadding={8}
            // Screen readers hear the action, not the drawn key chips
            aria-label={label}
            className="z-50"
          >
            <motion.div
              data-testid="feed-key-tooltip"
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.08 }}
              className={cn(
                // Key chips sit closer to the edge than text does
                keys?.length ? 'pr-1.5' : 'pr-2.5',
                'flex w-max items-center gap-2.5 whitespace-nowrap rounded-[9px] bg-foreground py-1.5 pl-2.5 text-[13px] font-medium leading-5 text-background shadow-[0_6px_20px_rgb(0_0_0/0.18)] dark:bg-secondary-active dark:text-foreground dark:shadow-[0_6px_20px_rgb(0_0_0/0.45)] dark:ring-1 dark:ring-border-strongest'
              )}
            >
              {label}
              {keys?.length ? (
                <span className="flex items-center gap-1">
                  {keys.map((key) => (
                    <kbd
                      key={key}
                      className="flex h-5 min-w-5 items-center justify-center rounded-[5px] bg-background/20 px-1.5 font-sans text-[12px] font-normal leading-none text-background dark:bg-foreground/15 dark:text-foreground"
                    >
                      {keyText(key)}
                    </kbd>
                  ))}
                </span>
              ) : null}
            </motion.div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      ) : null}
    </TooltipPrimitive.Root>
  );
};
